import React, { useState, useEffect } from 'react';
import {
    Box, Card, CardContent, Typography, Button, Chip, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Avatar, Divider, CircularProgress, Alert, Stack,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    LocalOffer as PromotionIcon,
    Inventory as ProductIcon,
    ShoppingCart as OrderIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon,
    CalendarToday as CalendarIcon,
    BarChart as UsageIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import promotionService from '../../../services/promotionService';
import { useCurrency } from '../../../hooks/useCurrency';

const fmtDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('fr-DZ', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const orderStatusColors = {
    PENDING: 'warning', PROCESSING: 'info', SHIPPED: 'primary',
    DELIVERED: 'success', CANCELLED: 'error',
    RETURN_REQUESTED: 'warning', EXCHANGE_REQUESTED: 'warning', RETURNED: 'secondary', EXCHANGED: 'secondary',
};
const paymentStatusColors = {
    PENDING: 'warning', PAID: 'success', FAILED: 'error', REFUNDED: 'default',
};

const PromotionDetails = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t } = useTranslation();

    const { format: fmt } = useCurrency();
    const [promotion, setPromotion] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchPromotion();
        fetchOrders();
    }, [id]);

    const fetchPromotion = async () => {
        try {
            const data = await promotionService.getPromotionById(id);
            setPromotion(data);
        } catch (err) {
            setError(err.response?.data?.message || t('promotions.fetchError'));
        } finally {
            setLoading(false);
        }
    };

    const fetchOrders = async () => {
        try {
            const data = await promotionService.getPromotionOrders(id);
            setOrders(data || []);
        } catch {
            setOrders([]);
        } finally {
            setOrdersLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/promotions')} sx={{ mt: 2 }}>
                    {t('common.back')}
                </Button>
            </Box>
        );
    }

    if (!promotion) return null;

    const usagePercent = promotion.usageLimit
        ? Math.round(((promotion.usageCount || 0) / promotion.usageLimit) * 100)
        : null;

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/promotions')}>
                        {t('common.back')}
                    </Button>
                    <Typography variant="h4" fontWeight="bold">{promotion.name}</Typography>
                    <Chip
                        icon={promotion.isActive ? <ActiveIcon /> : <InactiveIcon />}
                        label={promotion.isActive ? t('common.active') : t('common.inactive')}
                        color={promotion.isActive ? 'success' : 'default'}
                        size="small"
                    />
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/promotions/edit/${id}`)}
                >
                    {t('common.edit')}
                </Button>
            </Box>

            <Grid container spacing={3}>
                {/* ── Left column: promotion info ── */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <PromotionIcon color="primary" />
                                <Typography variant="h6">{t('promotions.promotion')}</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            <Stack spacing={1.5}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">{t('promotions_ext.stores')}</Typography>
                                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        <Chip label={promotion.storeName || '—'} size="small" color="primary" />
                                        {(promotion.additionalStoreNames || []).map((name) => (
                                            <Chip key={name} label={name} size="small" variant="outlined" />
                                        ))}
                                    </Box>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">{t('promotions.code')}</Typography>
                                    <Box sx={{ mt: 0.5 }}>
                                        {promotion.code ? (
                                            <Chip
                                                label={promotion.code}
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                                sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                                            />
                                        ) : <Typography variant="body2" color="text.secondary">{t('common.none')}</Typography>}
                                    </Box>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">{t('promotions.type')}</Typography>
                                    <Box sx={{ mt: 0.5 }}>
                                        <Chip
                                            label={t(`promotions.types.${promotion.type}`)}
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                        />
                                    </Box>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">{t('promotions.value')}</Typography>
                                    <Typography variant="h6" color="primary" fontWeight="bold">
                                        {promotion.type === 'PERCENTAGE'
                                            ? `${promotion.value}%`
                                            : fmt(promotion.value)}
                                    </Typography>
                                </Box>

                                {promotion.minimumAmount && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">{t('promotions.minOrderAmount')}</Typography>
                                        <Typography variant="body1">{fmt(promotion.minimumAmount)}</Typography>
                                    </Box>
                                )}

                                {promotion.customerName && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">{t('customers.customer')}</Typography>
                                        <Typography variant="body1">{promotion.customerName}</Typography>
                                    </Box>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>

                    {/* Date & Usage */}
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <CalendarIcon color="primary" />
                                <Typography variant="h6">{t('promotions.startDate')} & {t('promotions.usageCount')}</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            <Stack spacing={1.5}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">{t('promotions.startDate')}</Typography>
                                    <Typography variant="body2">{fmtDate(promotion.startDate)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">{t('promotions.endDate')}</Typography>
                                    <Typography variant="body2">{fmtDate(promotion.endDate)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">{t('promotions.usageCount')}</Typography>
                                    <Typography variant="body2">
                                        {promotion.usageCount ?? 0}
                                        {promotion.usageLimit ? ` / ${promotion.usageLimit}` : ` (${t('common.all')})`}
                                        {usagePercent !== null && (
                                            <Chip
                                                label={`${usagePercent}% used`}
                                                size="small"
                                                sx={{ ml: 1 }}
                                                color={usagePercent >= 90 ? 'error' : usagePercent >= 70 ? 'warning' : 'success'}
                                            />
                                        )}
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* ── Right column: products + orders ── */}
                <Grid item xs={12} md={8}>
                    {/* Products the promotion applies to */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <ProductIcon color="primary" />
                                <Typography variant="h6">
                                    {t('nav.products')}
                                    <Chip
                                        label={(promotion.productIds || []).length || t('common.all')}
                                        size="small"
                                        sx={{ ml: 1 }}
                                        color="primary"
                                        variant="outlined"
                                    />
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            {(!promotion.productIds || promotion.productIds.length === 0) ? (
                                <Alert severity="info" icon={false}>
                                    {t('common.all')}
                                </Alert>
                            ) : (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                                <TableCell>#</TableCell>
                                                <TableCell>{t('common.name')}</TableCell>
                                                <TableCell>{t('common.id')}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {promotion.productIds.map((pid, idx) => (
                                                <TableRow key={pid} hover
                                                    sx={{ cursor: 'pointer' }}
                                                    onClick={() => navigate(`/products/view/${pid}`)}
                                                >
                                                    <TableCell>{idx + 1}</TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.light', fontSize: 12 }}>
                                                                {(promotion.productNames?.[idx] || 'P').charAt(0).toUpperCase()}
                                                            </Avatar>
                                                            <Typography variant="body2" fontWeight="medium">
                                                                {promotion.productNames?.[idx] || '—'}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" color="text.secondary"
                                                            sx={{ fontFamily: 'monospace' }}>
                                                            {pid}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </CardContent>
                    </Card>

                    {/* Orders that used this promotion */}
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <OrderIcon color="primary" />
                                <Typography variant="h6">
                                    {t('nav.orders')}
                                    <Chip
                                        label={orders.length}
                                        size="small"
                                        sx={{ ml: 1 }}
                                        color="secondary"
                                        variant="outlined"
                                    />
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            {ordersLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                    <CircularProgress size={28} />
                                </Box>
                            ) : orders.length === 0 ? (
                                <Alert severity="info" icon={false}>
                                    {t('common.noData')}
                                </Alert>
                            ) : (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                                <TableCell>{t('orders.orderNumber')}</TableCell>
                                                <TableCell>{t('orders.customer')}</TableCell>
                                                <TableCell>{t('common.status')}</TableCell>
                                                <TableCell>{t('orders.paymentStatus')}</TableCell>
                                                <TableCell align="right">{t('orders.totalAmount')}</TableCell>
                                                <TableCell>{t('common.date')}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {orders.map((order) => (
                                                <TableRow key={order.id} hover
                                                    sx={{ cursor: 'pointer' }}
                                                    onClick={() => navigate(`/orders/${order.id}`)}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight="medium"
                                                            color="primary">
                                                            {order.orderNumber}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {order.customerName || (
                                                            <Typography variant="body2" color="text.secondary">
                                                                {t('orders.walkIn')}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={t(`orders.status.${order.status}`, order.status)}
                                                            size="small"
                                                            color={orderStatusColors[order.status] || 'default'}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={t(`orders.paymentStatuses.${order.paymentStatus}`, order.paymentStatus)}
                                                            size="small"
                                                            color={paymentStatusColors[order.paymentStatus] || 'default'}
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {fmt(order.totalAmount)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {fmtDate(order.orderedAt || order.createdAt)}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default PromotionDetails;
